import type {
  LifecycleActionVerdict,
  LifecycleRefusalReason,
  ProjectLifecycleState,
} from "@/api/hooks";

/**
 * The canonical lifecycle vocabulary's ONE table home (#651), on
 * `projectStatus.ts`'s exact discipline: exhaustive switches whose `never`
 * default fails the build when #650's contract enum grows, plus the runtime
 * half — a member this build has never heard of renders as itself instead of
 * as a blank.
 *
 * ## Nothing here is derived
 *
 * Each function maps ONE member of ONE projected enum to ONE string or tone.
 * The state comes from `ProjectOverview.lifecycle.state` / an index row's
 * `lifecycleState` — the same derivation on both, by the contract's own
 * promise — and no label is composed from a second field. The verdicts come
 * from `lifecycle.capabilities.*`; whether an action is offered is the
 * SERVER's answer, and these tables only put it in a clinician's words.
 *
 * ## `published` claims the artifact, never a runtime
 *
 * The contract pins what the state may mean: the publication chain completed
 * to the target-neutral artifact. The label says "Published" because the
 * ratified vocabulary does; the DESCRIPTION carries the boundary — a release
 * owner decides if and when anything reaches a live environment — so the one
 * word can never quietly grow into the live-state claim #29 forbids.
 */

/** The ratified state, in a clinician's words. */
export function lifecycleStateLabel(state: ProjectLifecycleState): string {
  switch (state) {
    case "draft":
      return "In draft";
    case "validated":
      return "Validated";
    case "preview_candidate":
      return "Test version ready";
    case "clinician_approved":
      return "Accepted";
    case "promotion_requested":
      return "Sent for review";
    case "published":
      return "Published";
    case "rejected":
      return "Not approved";
    case "expired":
      return "Test version expired";
    case "archived":
      return "Archived";
    default:
      return unrecognised(state);
  }
}

/**
 * The badge tone per state. `rejected` and `expired` are the two the clinician
 * must act on, so they carry the warning tone; everything else is progress or
 * a quiet terminal.
 */
export function lifecycleStateTone(
  state: ProjectLifecycleState,
): "success" | "warning" | "neutral" {
  switch (state) {
    case "validated":
    case "clinician_approved":
    case "published":
      return "success";
    case "rejected":
    case "expired":
      return "warning";
    case "draft":
    case "preview_candidate":
    case "promotion_requested":
    case "archived":
      return "neutral";
    default:
      unrecognised(state);
      return "neutral";
  }
}

/**
 * One sentence of context per state — what the state MEANS, never a promise
 * about what will happen next (`nextAction` and the verdicts own those).
 */
export function lifecycleStateDescription(state: ProjectLifecycleState): string {
  switch (state) {
    case "draft":
      return "The configuration is being worked on.";
    case "validated":
      return "The configuration passed authoritative validation and can be frozen as a test version.";
    case "preview_candidate":
      return "A test version is frozen and ready to preview as a patient.";
    case "clinician_approved":
      return "The current test version was accepted after preview.";
    case "promotion_requested":
      return "The accepted version was sent for review. A manager reviews it outside the editor.";
    case "published":
      return "The reviewed version was published. The project's release journey shows deployment progress and verification.";
    case "rejected":
      return "The publication attempt did not complete, or its automated review did not pass. The publication status below names the recovery.";
    case "expired":
      return "The current test version's validity window has ended. Freeze and validate a new version to continue.";
    case "archived":
      return "This project is archived and out of active use. Its history and evidence are retained.";
    default:
      return unrecognised(state);
  }
}

/**
 * The refusal reasons, in a clinician's words — the sentences the lane and
 * the dialogs render beside a withheld or refused action. Where a reason has
 * an already-ratified sentence elsewhere (`no_qualifying_preview` is the
 * lane's preview-first copy; `candidate_not_evidence_eligible` is the
 * eligibility copy's summary form) the wording matches it, because one
 * refusal must read the same wherever it appears.
 */
const LIFECYCLE_REFUSAL_COPY = {
  "lifecycle.capability_missing":
    "You are not authorized to do this for this project.",
  "lifecycle.project_archived":
    "This project is archived, so this action is not available.",
  "lifecycle.candidate_missing":
    "There is no frozen test version yet. Freeze and preview one first.",
  "lifecycle.candidate_not_evidence_eligible":
    "This version cannot be accepted for publication. Freeze a new version once the approved safety-rule catalogue is in place.",
  "lifecycle.candidate_expired":
    "This version's validity window has ended. Freeze and validate a new version, then preview and accept that one.",
  "lifecycle.candidate_superseded":
    "This test version was frozen from a previous configuration: the draft, an adopted library item or the base changed after it was frozen. Validate and freeze the current configuration, preview it, then accept that version.",
  "lifecycle.no_qualifying_preview":
    "Preview this version as a patient first, against this Project's current preview target, then accept it. A change of preview target also means previewing again.",
  "lifecycle.acceptance_missing":
    "Accept the current test version after previewing it. Only an accepted version can be sent for review.",
  "lifecycle.publication_in_flight":
    "A review of this version is already in progress.",
  "lifecycle.publication_recorded": "This version has already been published.",
} satisfies Record<LifecycleRefusalReason, string>;

/**
 * The sentence for one refusal reason. A reason this build has never heard of
 * renders as itself — visibly odd rather than silently blank, the same
 * fail-open refusal `projectStatus.unrecognised` documents.
 */
export function lifecycleRefusalCopy(reason: LifecycleRefusalReason): string {
  return (
    (LIFECYCLE_REFUSAL_COPY as Record<string, string>)[reason] ?? String(reason)
  );
}

/** The refusal reason a verdict carries, or null when it is permitted. */
export function refusalOf(
  verdict: LifecycleActionVerdict,
): LifecycleRefusalReason | null {
  return verdict.state === "refused" ? verdict.reason : null;
}

/** See `projectStatus.unrecognised` — the same runtime fail-visible arm. */
function unrecognised(member: never): string {
  return String(member);
}
